import React, { useState, useEffect, useRef } from "react";
import api from "../../api";
import InputField from "../../components/Form/InputField";
import Button from "../../components/Button";
import { FiUpload, FiDownload } from "react-icons/fi";
import { exportToExcel } from "../../utils/exportToExcel";

export default function DistanceDurationFromTo() {
  // استان‌ها
  const [provinces, setProvinces] = useState([]);
  // شهرهای مبدأ و مقصد
  const [originCities, setOriginCities] = useState([]);
  const [destCities, setDestCities] = useState([]);
  // فرم انتخاب شهر/استان
  const [formData, setFormData] = useState({
    originProvince: "",
    originCity: "",
    destProvince: "",
    destCity: ""
  });

  // سایر stateها
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState({ distance_km: "", duration_minutes: "" });
  const [file, setFile] = useState(null);
  const fileInputRef = useRef();

  // دریافت استان‌ها
  useEffect(() => {
    api.get("/accounts/provinces/")
      .then(res => setProvinces(res.data))
      .catch(err => console.error("Error loading provinces:", err));
  }, []);

  // دریافت شهرهای مبدأ با تغییر استان مبدأ
  useEffect(() => {
    if (!formData.originProvince) {
      setOriginCities([]);
      setFormData(prev => ({ ...prev, originCity: "" }));
      return;
    }
    api.get(`/accounts/cities/?province=${formData.originProvince}`)
      .then(res => setOriginCities(res.data))
      .catch(() => setOriginCities([]));
  }, [formData.originProvince]);

  // دریافت شهرهای مقصد با تغییر استان مقصد
  useEffect(() => {
    if (!formData.destProvince) {
      setDestCities([]);
      setFormData(prev => ({ ...prev, destCity: "" }));
      return;
    }
    api.get(`/accounts/cities/?province=${formData.destProvince}`)
      .then(res => setDestCities(res.data))
      .catch(() => setDestCities([]));
  }, [formData.destProvince]);

  // دریافت رکورد از-به با id شهرها
  const fetchRecord = async () => {
    const { originCity, destCity } = formData;
    if (!originCity || !destCity) {
      setRecord(null);
      return;
    }
    try {
      setLoading(true);
      const res = await api.get(
        `/operation/from-to/?origin=${originCity}&destination=${destCity}`
      );
      setRecord(res.data);
      setForm({
        distance_km: res.data.distance_km ?? "",
        duration_minutes: res.data.duration_minutes ?? "",
        duration_days: res.data.duration_days ?? "",
      });
    } catch (err) {
      setRecord(null);
      setForm({ distance_km: "", duration_minutes: "", duration_days:"" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecord();
    // eslint-disable-next-line
  }, [formData.originCity, formData.destCity]);

  // هندل تغییر فرم انتخاب شهر/استان
  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // اگر استان عوض شد، شهر مربوط را ریست کنیم
      ...(name === "originProvince" ? { originCity: "" } : {}),
      ...(name === "destProvince" ? { destCity: "" } : {})
    }));
  };

  // هندل تغییرات فرم مسافت/مدت
  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ارسال ویرایش رکورد
  const handleSubmit = async () => {
    if (!record?.id) return;
    try {
      await api.patch(`/operation/from-to/${record.id}/`, form);
      alert("ویرایش با موفقیت انجام شد");
    } catch (err) {
      alert("خطا در ویرایش");
    }
  };

  // اکسل - بارگذاری
  const handleOpenFileDialog = () => fileInputRef.current?.click();
  const handleFileSelected = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    setFile(selectedFile);

    const formExcel = new FormData();
    formExcel.append("file", selectedFile);
    try {
      setExcelLoading(true);
      await api.post("/operation/from-to/bulk-upload/", formExcel, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("بارگذاری فایل انجام شد");
    } catch {
      alert("خطا در بارگذاری فایل");
    } finally {
      setExcelLoading(false);
      setFile(null);
    }
  };

  // اکسل - دانلود
  const handleExportExcel = async () => {
    try {
      const { data } = await api.get("/operation/from-to/");
      if (!data.length) {
        alert("داده‌ای وجود ندارد");
        return;
      }
      // استخراج اطلاعات استان و شهر
      const excelData = data.map(row => ({
        "استان مبدأ": row.origin_detail?.province_name ?? "",
        "شهر مبدأ": row.origin_detail?.name ?? "",
        "استان مقصد": row.destination_detail?.province_name ?? "",
        "شهر مقصد": row.destination_detail?.name ?? "",
        "مسافت (کیلومتر)": row.distance_km ?? "",
        "مدت زمان (دقیقه)": row.duration_minutes ?? "",
        "مدت زمان (روز)": row.duration_days ?? "",
      }));
      exportToExcel(excelData, "from-to");
    } catch {
      alert("خطا در گرفتن خروجی");
    }
  };

  // رابط کاربری
  return (
    <div className="p-4 border rounded-xl shadow bg-white space-y-4">
      {/* اکشن اکسل */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Button
          variant="primary"
          icon={<FiUpload className="w-5 h-5 ml-2" />}
          onClick={handleOpenFileDialog}
        >
          {excelLoading ? "در حال بارگذاری..." : "بارگذاری فایل اکسل"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx"
          style={{ display: "none" }}
          onChange={handleFileSelected}
        />
        <Button
          variant="secondary"
          icon={<FiDownload className="w-5 h-5 ml-2" />}
          onClick={handleExportExcel}
        >
          دانلود فایل اکسل
        </Button>
        <div></div>
      </div>

      {/* فرم انتخاب استان + شهر مبدأ و مقصد */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* مبدأ */}
        <div className="space-y-2">
          {/* استان مبدأ */}
          <div className="grid grid-cols-3 items-center gap-3">
            <label className="text-xs font-medium text-gray-700">
              استان مبدأ <span className="text-red-500">*</span>
            </label>
            <select
              name="originProvince"
              value={formData.originProvince}
              onChange={handleSelectChange}
              className="col-span-2 bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 transition"
            >
              <option value="">انتخاب استان</option>
              {provinces.map(province => (
                <option key={province.id} value={province.id}>
                  {province.name}
                </option>
              ))}
            </select>
          </div>
          {/* شهر مبدأ */}
          <div className="grid grid-cols-3 items-center gap-3">
            <label className="text-xs font-medium text-gray-700">
              شهر مبدأ <span className="text-red-500">*</span>
            </label>
            <select
              name="originCity"
              value={formData.originCity}
              onChange={handleSelectChange}
              disabled={!formData.originProvince || !originCities.length}
              className="col-span-2 bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 transition disabled:opacity-50"
            >
              <option value="">انتخاب شهر</option>
              {originCities.map(city => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {/* مقصد */}
        <div className="space-y-2">
          {/* استان مقصد */}
          <div className="grid grid-cols-3 items-center gap-3">
            <label className="text-xs font-medium text-gray-700">
              استان مقصد <span className="text-red-500">*</span>
            </label>
            <select
              name="destProvince"
              value={formData.destProvince}
              onChange={handleSelectChange}
              className="col-span-2 bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 transition"
            >
              <option value="">انتخاب استان</option>
              {provinces.map(province => (
                <option key={province.id} value={province.id}>
                  {province.name}
                </option>
              ))}
            </select>
          </div>
          {/* شهر مقصد */}
          <div className="grid grid-cols-3 items-center gap-3">
            <label className="text-xs font-medium text-gray-700">
              شهر مقصد <span className="text-red-500">*</span>
            </label>
            <select
              name="destCity"
              value={formData.destCity}
              onChange={handleSelectChange}
              disabled={!formData.destProvince || !destCities.length}
              className="col-span-2 bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 transition disabled:opacity-50"
            >
              <option value="">انتخاب شهر</option>
              {destCities.map(city => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* نمایش ویرایش رکورد از-به */}
      {record && (
        <div className="p-4 border rounded bg-gray-50 space-y-4">
          <p className="font-medium">
            مسیر انتخاب شده:{" "}
            <span className="text-blue-700">
              {record.origin_detail?.name || ""}{" "}({record.origin_detail?.province_name || ""}){" "}
              -{" "}
              {record.destination_detail?.name || ""}{" "}({record.destination_detail?.province_name || ""})
            </span>
          </p>
          {/* فیلد مسافت */}
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex-1 w-full">
              <InputField
                  label="مسافت (کیلومتر)"
                  name="distance_km"
                  type="number"
                  value={form.distance_km}
                  onChange={handleFormChange}
              />
            </div>
            {/* فیلد مدت زمان */}
            <div className="flex-1 w-full">
              <InputField
                  label="مدت زمان (دقیقه)"
                  name="duration_minutes"
                  type="number"
                  value={form.duration_minutes}
                  onChange={handleFormChange}
              />
            </div>
            {/* فیلد مدت زمان (روز) */}
            <div className="flex-1 w-full">
              <InputField
                  label="مدت زمان (روز)"
                  name="duration_days"
                  type="number"
                  value={form.duration_days}
                  onChange={handleFormChange}
              />
            </div>
            </div>
            <Button variant="primary" onClick={handleSubmit}>
              ثبت تغییرات
            </Button>
          </div>
          )}
        </div>
      );
      }
